/**
 * Writes text to the clipboard and surfaces a success/failure notification.
 * Centralizes the "copy + toast" pattern used by browse-mode token / chip
 * interactions so the strings and behavior stay consistent.
 */

import { useCallback } from 'react';
import { useNotificationStore } from '@/stores/notificationStore';

async function writeToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator.clipboard?.writeText !== 'function') return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Returns an async fn that copies `text` to the clipboard and dispatches a
 * toast. `displayMessage` populates the success toast body and should be a
 * human-readable label for what was copied (e.g., the token title).
 */
export function useCopyWithToast() {
  const addNotification = useNotificationStore((s) => s.addNotification);

  return useCallback(
    async (text: string, displayMessage: string) => {
      const success = await writeToClipboard(text);
      addNotification(
        success
          ? { type: 'success', title: 'Copied to clipboard', message: displayMessage }
          : { type: 'error', title: 'Copy failed', message: 'Couldn’t write to the clipboard.' },
      );
    },
    [addNotification],
  );
}
